/* eslint-disable @typescript-eslint/no-explicit-any */
interface Station {
  id: string;
  name: string;
  siteName: string;
  customerName: string;
  roleName: string;
}

interface GTag {
  setUserProperties?: (user: { id: string; email: string; stations: Station[] }, location: string) => void;
  login?: () => void;
  campaignClick?: (campaignName: string, screen: string, button: string) => void;
  getPageName?: (path: string) => string;
}

/**
 * Check if cookie consent allows tracking
 * @returns true if cookies are allowed, false otherwise
 */
const isTrackingAllowed = (): boolean => {
  const consentCookieValue = document.cookie
    ?.split('; ')
    ?.find(cookie => cookie.includes('cookieconsent_status'))
    ?.split('=')?.[1];
  return consentCookieValue === 'allow';
};

const gtag: GTag = {};

gtag.setUserProperties = (user, location) => {
  // Don't track if cookies are declined
  if (!isTrackingAllowed()) {
    return;
  }

  const { id, email, stations } = user ?? {};
  const currentStationId = extractStationIdFromPath(location);
  const currentStation = stations?.find(station => station?.id === currentStationId);
  const customerSiteStation = currentStation
    ? `${currentStation?.customerName}-${currentStation?.siteName}-${currentStation?.name}`
    : undefined;

  const pageTitle = gtag.getPageName?.(location);
  (window as any).gtag('config', import.meta.env.VITE_ANALYTICS_MEASUREMENT_ID, {
    user_id: id,
    user_email: email,
    station_id: currentStationId,
    customer_site_station: customerSiteStation,
    role: currentStation?.roleName,
    page_title: pageTitle,
    send_page_view: false,
  });
};

gtag.login = () => {
  // Don't track if cookies are declined
  if (!isTrackingAllowed()) {
    return;
  }
  (window as any).gtag('event', 'login');
};

gtag.campaignClick = (campaignName, screen, button) => {
  // Don't track if cookies are declined
  if (!isTrackingAllowed()) {
    return;
  }
  (window as any).gtag('event', campaignName, {
    componentScreen: screen,
    componentButton: button,
  });
};

function extractStationIdFromPath(path) {
  const pathSplit = path.split('/');
  return pathSplit[pathSplit.indexOf('station') + 1];
}

gtag.getPageName = path => {
  const pagesRegex = [
    { regex: /.*\/tab\/map/, pageName: 'P&ID' },
    { regex: /.*\/tab\/trends/, pageName: 'Trends' },
    { regex: /.*\/tab\/investigation/, pageName: 'Investigation Dashboard' },
    { regex: /.*\/tab\/control-center/, pageName: 'Station Control Center' },
    { regex: /.*\/offering-page/, pageName: 'Offering Page' },
    { regex: /.*\/value\/power/, pageName: 'Value Dashboard' },
    { regex: /.*\/settings\/users.*\/edit/, pageName: 'User Management - Edit' },
    { regex: /.*\/settings\/users\/new/, pageName: 'User Management - Create' },
    { regex: /.*\/settings\/users/, pageName: 'User Management' },
    { regex: /.*\/rules\/custom.*\/edit/, pageName: 'Custom Alert Rules Management - Edit' },
    { regex: /.*\/rules\/custom/, pageName: 'Custom Alert Rules Management' },
    { regex: /.*\/rules\/manufacturer.*\/edit/, pageName: 'Manufacturer Alert Rules Management - Edit' },
    { regex: /.*\/rules\/manufacturer/, pageName: 'Manufacturer Alert Rules Management' },
    { regex: /.*\/rules\/new/, pageName: 'Alert Rules Management - Create' },
  ];
  return pagesRegex.find(page => path?.match(page.regex))?.pageName || null;
};

export default gtag;
